import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel
} from '@mui/material';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Simple inline slugify (no external dependency needed)
const slugify = (text) => text.toString().toLowerCase().trim()
    .replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/--+/g, '-');

const BlogPostDialog = ({ open, onClose, post, mode, onSave, categories }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const [formData, setFormData] = useState({
        slug: '',
        title_en: '', title_fi: '', title_de: '', title_fr: '',
        desc_en: '', desc_fi: '', desc_de: '', desc_fr: '',
        content_en: '', content_fi: '',
        image: '',
        category_id: 0,
        status: 1
    });

    const languages = [
        { code: 'en', label: 'English' },
        { code: 'fi', label: 'Finnish' },
        { code: 'de', label: 'German' },
        { code: 'fr', label: 'French' }
    ];

    const quillModules = useMemo(() => ({
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ color: [] }, { background: [] }],
            ['link', 'image', 'blockquote', 'code-block'],
            [{ align: [] }],
            ['clean']
        ]
    }), []);

    useEffect(() => {
        if (post && mode === 'edit') {
            setFormData({
                slug: post.slug || '',
                title_en: post.title_en || '', title_fi: post.title_fi || '',
                title_de: post.title_de || '', title_fr: post.title_fr || '',
                desc_en: post.desc_en || '', desc_fi: post.desc_fi || '',
                desc_de: post.desc_de || '', desc_fr: post.desc_fr || '',
                content_en: post.content_en || '', content_fi: post.content_fi || '',
                image: post.image || '',
                category_id: post.category_id || 0,
                status: post.status ?? 1
            });
        } else {
            setFormData({
                slug: '', title_en: '', title_fi: '', title_de: '', title_fr: '',
                desc_en: '', desc_fi: '', desc_de: '', desc_fr: '',
                content_en: '', content_fi: '',
                image: '', category_id: 0, status: 1
            });
        }
        setError('');
        setSuccess(false);
        setActiveTab(0);
    }, [post, mode, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            // Auto-generate slug from English title
            if (name === 'title_en' && mode === 'add') {
                updated.slug = slugify(value, { lower: true, strict: true });
            }
            return updated;
        });
    };

    const handleStatusChange = (e) => {
        setFormData(prev => ({ ...prev, status: e.target.checked ? 1 : 0 }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setError('');

        const uploadData = new FormData();
        uploadData.append('image', file);

        try {
            const response = await fetch('https://api.elnaf.fi/api/upload_blog_image.php', {
                method: 'POST',
                body: uploadData
            });
            const data = await response.json();
            if (data.status === 'success') {
                setFormData(prev => ({ ...prev, image: data.url }));
            } else {
                setError(data.message || 'Image upload failed.');
            }
        } catch (err) {
            console.error('Upload error:', err);
            setError('Image upload failed. Please try again.');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const handleSubmit = async () => {
        if (!formData.title_en.trim()) {
            setError('English title is required.');
            return;
        }
        if (!formData.slug.trim()) {
            setError('Slug is required.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const url = mode === 'add'
                ? 'https://api.elnaf.fi/api/add_blog_post.php'
                : 'https://api.elnaf.fi/api/update_blog_post.php';

            const payload = { ...formData };
            if (mode === 'edit' && post) {
                payload.id = post.id;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const responseText = await response.text();
            let data;
            try {
                data = JSON.parse(responseText);
            } catch (e) {
                setError('Invalid response from server.');
                setLoading(false);
                return;
            }

            if (data.status === 'success') {
                setSuccess(true);
                setTimeout(() => {
                    if (onSave) onSave();
                }, 1200);
            } else {
                setError(data.message || 'An error occurred.');
            }
        } catch (err) {
            console.error('Error:', err);
            setError('Network error.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading && !success) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                {mode === 'add' ? 'New Blog Post' : 'Edit Blog Post'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>
                        {mode === 'add' ? 'Blog post added successfully!' : 'Blog post updated successfully!'}
                    </Alert>}

                    {/* Əsas Məlumatlar */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                        <TextField
                            label="Slug"
                            name="slug"
                            value={formData.slug}
                            onChange={handleChange}
                            sx={{ flex: 1 }}
                            helperText="URL path (e.g: my-blog-post)"
                        />
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Category</InputLabel>
                            <Select
                                name="category_id"
                                value={formData.category_id}
                                onChange={handleChange}
                                label="Category"
                            >
                                <MenuItem value={0}>Uncategorized</MenuItem>
                                {categories.map(cat => (
                                    <MenuItem key={cat.id} value={cat.id}>{cat.name_en}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={<Switch checked={formData.status === 1} onChange={handleStatusChange} />}
                            label={formData.status === 1 ? 'Active' : 'Inactive'}
                        />
                    </Box>

                    {/* Image: Upload or URL */}
                    <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <strong style={{ fontSize: '0.9rem' }}>Cover Image</strong>
                            {uploading && <CircularProgress size={18} />}
                        </Box>

                        {/* Preview */}
                        {formData.image && (
                            <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
                                <img src={formData.image} alt="Preview" style={{ maxHeight: 150, borderRadius: 8, border: '1px solid #eee' }} />
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                                    sx={{ position: 'absolute', top: 4, right: 4, minWidth: 28, p: 0.5, bgcolor: 'rgba(255,255,255,0.9)', fontSize: '0.7rem' }}
                                >✕</Button>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            {/* File Upload */}
                            <Button
                                variant="outlined"
                                component="label"
                                disabled={uploading}
                                sx={{ minWidth: 140, height: 40 }}
                            >
                                {uploading ? 'Uploading...' : 'Choose File'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    onChange={handleImageUpload}
                                />
                            </Button>

                            {/* OR URL */}
                            <TextField
                                fullWidth
                                label="or paste Image URL"
                                name="image"
                                value={formData.image}
                                onChange={handleChange}
                                size="small"
                                helperText="Upload a file or paste an external URL"
                            />
                        </Box>
                    </Box>

                    {/* Language tabs */}
                    <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        {languages.map((lang) => (
                            <Tab key={lang.code} label={lang.label} />
                        ))}
                    </Tabs>

                    {languages.map((lang, index) => (
                        <Box key={lang.code} hidden={activeTab !== index} sx={{ pt: 1 }}>
                            <TextField
                                fullWidth
                                label={`Title (${lang.label})`}
                                name={`title_${lang.code}`}
                                value={formData[`title_${lang.code}`]}
                                onChange={handleChange}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                label={`Short Description (${lang.label})`}
                                name={`desc_${lang.code}`}
                                value={formData[`desc_${lang.code}`]}
                                onChange={handleChange}
                                multiline
                                rows={2}
                                sx={{ mb: 2 }}
                            />
                            {(lang.code === 'en' || lang.code === 'fi') && (
                                <Box sx={{ mb: 2 }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 500, color: '#666', marginBottom: 4, display: 'block' }}>
                                        Full Content ({lang.label})
                                    </label>
                                    <Box sx={{ '.ql-container': { minHeight: 250, fontSize: '0.95rem' }, '.ql-editor': { minHeight: 250 } }}>
                                        <ReactQuill
                                            theme="snow"
                                            value={formData[`content_${lang.code}`] || ''}
                                            onChange={(value) => setFormData(prev => ({ ...prev, [`content_${lang.code}`]: value }))}
                                            modules={quillModules}
                                            placeholder={`Write full content in ${lang.label}...`}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading || success}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || success}
                    startIcon={loading && <CircularProgress size={20} />}
                >
                    {success ? 'Saved!' : (mode === 'add' ? 'Add Post' : 'Save Changes')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BlogPostDialog;
